import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, Plus, Edit2, Trash2, Eye, Truck, FileText, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const LrReceiptList = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [docketNo, setDocketNo] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const auth = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' }), []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [rRes, dRes] = await Promise.all([
          fetch(`${backendUrl}/api/lr-receipts?docket_id=${docketId}`, { headers: auth() }),
          fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: auth() }),
        ]);
        if (rRes.ok) setReceipts(await rRes.json());
        if (dRes.ok) { const d = await dRes.json(); setDocketNo(d.system_docket_no || docketId); }
      } catch { toast({ title: 'Error', description: 'Failed to load LR Receipts', variant: 'destructive' }); }
      finally { setLoading(false); }
    })();
  }, [docketId, backendUrl, auth, toast]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this LR Receipt?')) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${backendUrl}/api/lr-receipts/${id}`, { method: 'DELETE', headers: auth() });
      if (!res.ok) throw new Error();
      toast({ title: 'Deleted' });
      setReceipts(prev => prev.filter(r => r.id !== id));
    } catch { toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' }); }
    finally { setDeletingId(null); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-full mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/dockets/edit/${docketId}`)}>
              <ArrowLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Truck className="w-5 h-5 text-orange-500" />LR Receipts
              </h1>
              <p className="text-xs text-gray-500">Docket: {docketNo}</p>
            </div>
          </div>
          <Button onClick={() => navigate(`/dockets/${docketId}/lr-receipts/new`)} className="bg-orange-600 hover:bg-orange-700" data-testid="add-lr-btn">
            <Plus className="w-4 h-4 mr-2" />Add New
          </Button>
        </div>

        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {receipts.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No LR Receipts yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "Add New" to create the first LR Receipt.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lr-list-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['Sr.', 'Job No.', 'Service Tax By', 'Insurance By', 'Data Logger No.', 'Date & Time', 'Temp', 'CN No.', 'CN Date', 'Vehicle No', 'Delivery At', 'From', 'To', 'Consignor', 'Consignee', 'Action'].map(h => (
                      <th key={h} className="text-left px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((r, idx) => (
                    <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{r.job_no || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.service_tax_payable_by || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.insurance_taken_by || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.data_logger_no || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{[r.date, r.time].filter(Boolean).join(' ') || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.temp || '—'}</td>
                      <td className="px-3 py-2 font-mono text-orange-700">{r.consignment_note_no || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.consignment_note_date || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.vehicle_no || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[120px] truncate">{r.delivery_at || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[120px] truncate">{r.from_location || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[120px] truncate">{r.to_location || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[150px] truncate">{(r.consignor_name_address || '').split('\n')[0] || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[150px] truncate">{(r.consignee_name_address || '').split('\n')[0] || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <button onClick={() => navigate(`/dockets/${docketId}/lr-receipts/${r.id}/print`)} className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100" title="View/Print" data-testid={`view-lr-${idx}`}><Eye className="w-3.5 h-3.5" /></button>
                          <button onClick={() => navigate(`/dockets/${docketId}/lr-receipts/${r.id}/edit`)} className="p-1.5 rounded bg-amber-50 text-amber-600 hover:bg-amber-100" title="Edit" data-testid={`edit-lr-${idx}`}><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleDelete(r.id)} disabled={deletingId === r.id} className="p-1.5 rounded bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50" title="Delete" data-testid={`delete-lr-${idx}`}>
                            {deletingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default LrReceiptList;
